import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Table, TableHead, TableRow, TableCell,
  TableBody, Radio, FormControlLabel, RadioGroup, Button, Typography, IconButton,
  Alert, CircularProgress, Backdrop
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import styles from '../PurchaseOrderTable/index.module.css';
import { regeneratePurchaseOrder } from '../../../../services/purchaseOrder';
import toast, { Toaster } from 'react-hot-toast';

const RegeneratePOsPopup = ({ open, onClose, purchaseOrders, fetchPurchaseOrders }) => {
  const [selectedActions, setSelectedActions] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [regenerationResults, setRegenerationResults] = useState([]);
  const [pendingConsolidation, setPendingConsolidation] = useState(null);

  useEffect(() => {
    if (purchaseOrders?.purchaseOrders) {
      const initial = purchaseOrders.purchaseOrders.reduce((acc, po) => {
        acc[po.id] = 'regenerateAs';
        return acc;
      }, {});
      setSelectedActions(initial);
    }
  }, [purchaseOrders]);

  const handleActionChange = (poId, value) => {
    if (submitting) return; // lock while submitting
    setPendingConsolidation(null);
    setSelectedActions(prev => ({ ...prev, [poId]: value }));
  };

  const handleClose = () => {
    if (submitting) return; // prevent closing while submitting
    setRegenerationResults([]);
    setPendingConsolidation(null);
    onClose?.();
  };

  const handleRegenerateAPI = async (confirmation = null) => {
    const confirmed = confirmation?.groupKey ? confirmation : null;
    if (submitting) return;
    try {
      setSubmitting(true);
      setPendingConsolidation(null);
      setRegenerationResults([]);
      const getPoLabel = (row) => row?.poNumber || row?.id || "unknown";

      const mapToServer = (ui) => {
        if (ui === 'createNew') return { action: 'Create new Po', actionString: 'create' };
        if (ui === 'regenerateAs') return { action: 'Regenerate Po', actionString: 'regenerate' };
        return null;
      };

      let payload = Object.keys(selectedActions)
        .map((poId) => {
          const mapped = mapToServer(selectedActions[poId]);
          if (!mapped) return null;
          return {
            id: Number(poId),
            action: mapped.action,
            actionString: mapped.actionString,
          };
        })
        .filter(Boolean);

      // A confirmed replacement replays only the two named source POs.
      // Other POs selected earlier must not be regenerated a second time.
      if (confirmed) {
        const ids = new Set((confirmed.sourcePurchaseOrders || []).map((po) => Number(po.id)));
        payload = payload.filter((po) => ids.has(po.id) && po.actionString === 'regenerate');
        if (!payload.length && confirmed.sourcePurchaseOrders?.length) {
          const first = confirmed.sourcePurchaseOrders[0];
          payload = [{ id: Number(first.id), action: 'Regenerate Po', actionString: 'regenerate' }];
        }
      }

      if (payload.length === 0) {
        toast('Nothing to do.');
        return;
      }

      const response = await regeneratePurchaseOrder({
        purchaseOrders: payload,
        ...(confirmed ? { confirmLinkedSupplierConsolidation: confirmed } : {}),
      });
      await fetchPurchaseOrders();
      const results = Array.isArray(response?.results) ? response.results : [];
      setRegenerationResults(results);
      const approval = results.find((r) =>
        r?.errorCode === 'PO_LINKED_SUPPLIER_CONSOLIDATION_CONFIRMATION_REQUIRED' && r?.details
      );
      if (approval) setPendingConsolidation(approval.details);
      const failed = results.filter((r) => r?.status === 'failed' &&
        r?.errorCode !== 'PO_LINKED_SUPPLIER_CONSOLIDATION_CONFIRMATION_REQUIRED');
      const succeeded = results.filter((r) => r?.status === 'success');

      if (!results.length) {
        toast.success('Purchase orders processed.');
        onClose?.();
        return;
      }

      if (succeeded.length > 0) {
        toast.success(`Processed ${succeeded.length} purchase order${succeeded.length > 1 ? 's' : ''}.`);
      }

      failed.forEach((row) => {
        const poLabel = getPoLabel(row);
        if (row?.errorCode === 'ambiguous-product') {
          toast.error(
            `PO #${poLabel}: ambiguous product lines detected. Please resave the sales order so each line has an orderItemId.`
          );
          return;
        }
        if (row?.errorCode === 'no-matching-order-items') {
          toast.error(
            `PO #${poLabel}: no matching order items were found. Please verify line mapping data and retry.`
          );
          return;
        }
        toast.error(`PO #${poLabel}: ${row?.message || 'Failed to process.'}`);
      });

      // Keep the dialog open to show the per-PO results.
    } catch (error) {
      console.error(error);
      const results = error?.response?.data?.results;
      if (Array.isArray(results) && results.length) {
        setRegenerationResults(results);
        const approval = results.find((r) =>
          r?.errorCode === 'PO_LINKED_SUPPLIER_CONSOLIDATION_CONFIRMATION_REQUIRED' && r?.details
        );
        if (approval) setPendingConsolidation(approval.details);
        results
          .filter((r) => r?.status === 'failed' &&
            r?.errorCode !== 'PO_LINKED_SUPPLIER_CONSOLIDATION_CONFIRMATION_REQUIRED')
          .forEach((row) => {
            const poLabel = row?.poNumber || row?.id || "unknown";
            if (row?.errorCode === 'ambiguous-product') {
              toast.error(
                `PO #${poLabel}: ambiguous product lines detected. Please resave the sales order so each line has an orderItemId.`
              );
              return;
            }
            if (row?.errorCode === 'no-matching-order-items') {
              toast.error(
                `PO #${poLabel}: no matching order items were found. Please verify line mapping data and retry.`
              );
              return;
            }
            toast.error(`PO #${poLabel}: ${row?.message || 'Failed to process.'}`);
          });
      } else {
        toast.error(error?.response?.data?.message || 'Failed to regenerate purchase orders');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const purchaseOrderList = useMemo(() => {
    if (!Array.isArray(purchaseOrders?.purchaseOrders)) return [];
    return purchaseOrders.purchaseOrders.map((po) => {
      const items = po.items.map((item) => {
        const price = item.pricings?.reduce((sum, p) => sum + Number(p.Total || 0), 0) || 0;
        const currency = item.pricings?.[0]?.Currency || 'USD';
        return {
          itemId: item.id,
          itemName: item.Name,
          quantity: item.Quantity,
          price,
          currency,
          imageUrl: item.ImageUrl,
        };
      });
      return {
        poId: po.id,
        poNumber: po.poNumber,
        supplierStatus: po.supplierStatus,
        productionRep: po.productionRep,
        proofStatus: po.proofStatus,
        productionRepStatus: po.productionRepStatus,
        supplierName: po.supplierInfo?.supplierName || '',
        supplierEmail: po.supplierInfo?.supplierEmail || '',
        orderId: po.orderId,
        clientContactEmail: po.clientContactEmail,
        items,
      };
    });
  }, [purchaseOrders]);

  return (
    <>
      <Toaster position="top-right" />
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md"
        PaperProps={{ style: { minHeight: '80vh' } }}>
        <DialogTitle className="create-order-dialog-title">
          <Typography variant="h6">Regenerate Project PO</Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={submitting}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size="large">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className="create-order-dialog-content ">
          <Alert severity="info" sx={{ mb: 2 }}>
            Please note: due to PDF generation, each PO may take upwards of 30s to generate successfully. Thank you for your patience.
          </Alert>

          {pendingConsolidation && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Supplier association updated — confirm PO replacement
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                These POs now share a verified ASI/Core supplier and compatible purchasing route.
                Regeneration will create one new combined PO and archive the originals:
              </Typography>
              <Typography variant="body2" sx={{ my: 1 }}>
                {(pendingConsolidation.sourcePurchaseOrders || []).map((po) =>
                  `${po.poNumber || po.id} (${po.supplierStatus || 'Unknown status'})`
                ).join(' + ')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Item IDs: {(pendingConsolidation.combinedOrderItemIds || []).join(', ')}.
                The originals and their PDF/send history remain available in Archived POs.
                The replacement starts as a new PO and must be reviewed before sending.
              </Typography>
              <Button size="small" color="warning" variant="contained"
                disabled={submitting}
                onClick={() => handleRegenerateAPI(pendingConsolidation)}>
                Confirm and replace POs
              </Button>
              <Button size="small" onClick={() => setPendingConsolidation(null)} disabled={submitting} sx={{ ml: 1 }}>
                Cancel
              </Button>
            </Alert>
          )}

          <Table stickyHeader className={styles.table}>
            <TableHead>
              <TableRow className={styles.tableRow}>
                <TableCell className={styles.tableHeaderCell}>Existing PO #</TableCell>
                <TableCell className={styles.tableHeaderCell}>PO Details</TableCell>
                <TableCell className={styles.tableHeaderCell}>Status</TableCell>
                <TableCell className={styles.tableHeaderCell}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purchaseOrderList?.map((po) => (
                <TableRow key={po.poId} className={styles.row}>
                  <TableCell sx={{ textAlign: 'center' }}>{po.poNumber}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>{po.supplierName || 'N/A'}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>{po.supplierStatus || 'N/A'}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <RadioGroup
                      name={`action-${po.poId}`}
                      value={selectedActions[po.poId] || 'regenerateAs'}
                      onChange={(e) => handleActionChange(po.poId, e.target.value)}
                    >
                      <FormControlLabel value="createNew" control={<Radio />} label="Create new PO#" disabled={submitting} />
                      <FormControlLabel value="dontRegenerate" control={<Radio />} label="Don't regenerate" disabled={submitting} />
                      <FormControlLabel value="regenerateAs" control={<Radio />} label={`Regenerate as ${po.poNumber}`} disabled={submitting} />
                    </RadioGroup>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {regenerationResults.some((row) => (row?.status === 'failed' && row?.errorCode !== 'PO_LINKED_SUPPLIER_CONSOLIDATION_CONFIRMATION_REQUIRED') || row?.proofMappingNeedsAttention === true) && (
            <div style={{ marginTop: 16 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Regeneration results</Typography>
              {regenerationResults.filter((row) => (row?.status === 'failed' && row?.errorCode !== 'PO_LINKED_SUPPLIER_CONSOLIDATION_CONFIRMATION_REQUIRED') || row?.proofMappingNeedsAttention === true).map((row, index) => {
                const poLabel = row?.poNumber || row?.id || 'unknown';
                const detail = row?.proofMappingDetails;
                const needsAttention = row?.status === 'success' && row?.proofMappingNeedsAttention === true;
                return (
                  <Alert key={`${row?.id || index}-${index}`} severity={row?.status === 'failed' ? 'error' : needsAttention ? 'info' : 'success'} sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      PO #{poLabel}: {row?.status === 'failed' ? 'Regeneration failed' : 'Regenerated successfully'}
                    </Typography>
                    {row?.status === 'failed' ? (
                      <Typography variant="body2">{row?.message || 'Could not process this purchase order.'}</Typography>
                    ) : needsAttention ? (
                      <>
                        <Typography variant="body2">
                          Artwork matching found {detail?.matched ?? 0} of {detail?.required ?? 0} required references; {detail?.unmatched ?? 0} could not be matched.
                        </Typography>
                        <Typography variant="body2">
                          Existing proof attachments, approval status and readiness were preserved. You can review the artwork mapping when convenient.
                        </Typography>
                      </>
                    ) : null}
                  </Alert>
                );
              })}
            </div>
          )}
        </DialogContent>

        <Button
          sx={{ width: 160, m: '0 0 10px 10px' }}
          onClick={() => handleRegenerateAPI()}
          disabled={submitting}
          variant="contained"
          startIcon={submitting ? <CircularProgress size={18} /> : null}
        >
          {submitting ? 'Processing…' : 'Regenerate'}
        </Button>

        {/* Full-screen lock while submitting */}
        <Backdrop open={submitting} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}>
          <CircularProgress />
        </Backdrop>
      </Dialog>
    </>
  );
};

export default RegeneratePOsPopup;
